import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img src="/qnc-logo.png" alt="QNC Company Ltd" className="h-20 w-auto" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <div className="relative group">
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                Services
              </button>
              {/* Dropdown for individual services */}
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button
                    onClick={() => scrollToSection('construction-services')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                  >
                    Construction Services
                  </button>
                  <button
                    onClick={() => scrollToSection('prefab-steel-buildings')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                  >
                    Prefab Steel Buildings
                  </button>
                  <button
                    onClick={() => scrollToSection('generator-solutions')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                  >
                    Generator Solutions
                  </button>
                  <button
                    onClick={() => scrollToSection('tools-and-equipment')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                  >
                    Tools & Equipment
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('generator-tool')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
            >
              Power Generator
            </button>
            <button
              onClick={() => scrollToSection('projects')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
            >
              Projects
            </button>
            <Link to="/sales-rep">
              <Button
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors duration-200"
              >
                Join Our Team
              </Button>
            </Link>
            <Link to="/login">
              <button
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Portal Login
              </button>
            </Link>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200"
            >
              Contact Us
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-2">
              <button
                onClick={() => scrollToSection('services')}
                className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 px-4 py-2"
              >
                All Services
              </button>
              <button
                onClick={() => scrollToSection('construction-services')}
                className="text-left text-gray-600 hover:text-blue-600 text-sm transition-colors duration-200 px-6 py-2"
              >
                Construction Services
              </button>
              <button
                onClick={() => scrollToSection('prefab-steel-buildings')}
                className="text-left text-gray-600 hover:text-blue-600 text-sm transition-colors duration-200 px-6 py-2"
              >
                Prefab Steel Buildings
              </button>
              <button
                onClick={() => scrollToSection('generator-solutions')}
                className="text-left text-gray-600 hover:text-blue-600 text-sm transition-colors duration-200 px-6 py-2"
              >
                Generator Solutions
              </button>
              <button
                onClick={() => scrollToSection('tools-and-equipment')}
                className="text-left text-gray-600 hover:text-blue-600 text-sm transition-colors duration-200 px-6 py-2"
              >
                Tools & Equipment
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 px-4 py-2"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('generator-tool')}
                className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 px-4 py-2"
              >
                Power Generator
              </button>
              <button
                onClick={() => scrollToSection('projects')}
                className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 px-4 py-2"
              >
                Projects
              </button>
              <Link to="/sales-rep" className="mx-4 mb-2">
                <Button
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 w-full"
                >
                  Join Our Team
                </Button>
              </Link>
              <Button
                onClick={() => scrollToSection('contact')}
                className="mx-4 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Contact Us
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;